// utils/urlHelper.ts - Enhanced with error handling

import React, { useEffect, useState } from "react";

/**
 * Application configuration
 */
const APP_CONFIG = {
  API_GATEWAY_URL: 'http://localhost:3000',
  ADMIN_SERVICE_URL: 'http://localhost:3001',
} as const;

/**
 * Build full asset URL for serving from API Gateway
 * @param assetPath - Relative asset path (e.g., "/v1/assets/images/user/image.jpg")
 * @returns Full URL or null if path is invalid
 */
export const buildAssetUrl = (assetPath: string | null | undefined): string | null => {
  if (!assetPath || typeof assetPath !== 'string') {
    return null;
  }

  // If already full URL, return as is
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath;
  }

  // Ensure path starts with /
  const cleanPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  
  return `${APP_CONFIG.API_GATEWAY_URL}${cleanPath}`;
};

/**
 * Build full image URL specifically for profile images with fallback
 * @param imagePath - Image path from database
 * @param fallbackUrls - Array of fallback URLs to try
 * @returns Full image URL or null
 */
export const buildImageUrl = (
  imagePath: string | null | undefined, 
  fallbackUrls: string[] = []
): string | null => {
  const primaryUrl = buildAssetUrl(imagePath);
  
  if (primaryUrl) {
    return primaryUrl;
  }
  
  // Try fallback URLs
  for (const fallback of fallbackUrls) {
    if (fallback && typeof fallback === 'string') {
      return buildAssetUrl(fallback);
    }
  }
  
  return null;
};

/**
 * Check if image URL is accessible (client-side)
 * @param imageUrl - Image URL to check
 * @returns Promise<boolean>
 */
export const checkImageAccessibility = (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      img.onabort = null;
    };
    
    img.onload = () => {
      cleanup();
      resolve(true);
    };
    
    img.onerror = () => {
      cleanup();
      resolve(false);
    };
    
    img.onabort = () => {
      cleanup();
      resolve(false);
    };
    
    // Set timeout to avoid hanging
    setTimeout(() => {
      cleanup();
      resolve(false);
    }, 5000);
    
    img.src = imageUrl;
  });
};

/**
 * React hook for safe image loading with fallbacks
 * @param imagePath - Primary image path
 * @param fallbacks - Array of fallback image paths
 * @returns Object with image state and error handling
 */
export const useSafeImage = (
  imagePath: string | null | undefined,
  fallbacks: string[] = []
) => {
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(-1);
  
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setFallbackIndex(-1);
    
    const tryLoadImage = async (url: string | null): Promise<boolean> => {
      if (!url) return false;
      
      try {
        const isAccessible = await checkImageAccessibility(url);
        if (isAccessible) {
          setCurrentImageUrl(url);
          setIsLoading(false);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };
    
    const loadImageWithFallbacks = async () => {
      // Try primary image
      const primaryUrl = buildImageUrl(imagePath);
      if (await tryLoadImage(primaryUrl)) {
        return;
      }
      
      // Try fallbacks
      for (let i = 0; i < fallbacks.length; i++) {
        const fallbackUrl = buildImageUrl(fallbacks[i]);
        if (await tryLoadImage(fallbackUrl)) {
          setFallbackIndex(i);
          return;
        }
      }
      
      // All failed
      setCurrentImageUrl(null);
      setHasError(true);
      setIsLoading(false);
    };
    
    loadImageWithFallbacks();
  }, [imagePath, fallbacks]);
  
  return {
    imageUrl: currentImageUrl,
    isLoading,
    hasError,
    isUsingFallback: fallbackIndex >= 0,
    fallbackIndex
  };
};

/**
 * Build document/file URL
 * @param filePath - File path from database
 * @returns Full file URL or null
 */
export const buildFileUrl = (filePath: string | null | undefined): string | null => {
  return buildAssetUrl(filePath);
};

/**
 * Build API endpoint URL
 * @param endpoint - API endpoint (e.g., "/api/v1/users")
 * @param useAdminService - Whether to use admin service URL instead of gateway
 * @returns Full API URL
 */
export const buildApiUrl = (endpoint: string, useAdminService: boolean = false): string => {
  const baseUrl = useAdminService ? APP_CONFIG.ADMIN_SERVICE_URL : APP_CONFIG.API_GATEWAY_URL;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Extract filename from asset path
 * @param assetPath - Asset path
 * @returns Filename or null
 */
export const extractFilename = (assetPath: string | null | undefined): string | null => {
  if (!assetPath) return null;
  const segments = assetPath.split('/');
  return segments[segments.length - 1] || null;
};

/**
 * Get file extension from asset path
 * @param assetPath - Asset path
 * @returns File extension (without dot) or null
 */
export const getFileExtension = (assetPath: string | null | undefined): string | null => {
  const filename = extractFilename(assetPath);
  if (!filename) return null;
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex > 0 ? filename.substring(dotIndex + 1).toLowerCase() : null;
};

/**
 * Check if file is an image based on extension
 * @param assetPath - Asset path
 * @returns boolean
 */
export const isImageFile = (assetPath: string | null | undefined): boolean => {
  const extension = getFileExtension(assetPath);
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return extension ? imageExtensions.includes(extension) : false;
};

/**
 * Default export with all utilities
 */
export default {
  buildAssetUrl,
  buildImageUrl,
  buildFileUrl,
  buildApiUrl,
  extractFilename,
  getFileExtension,
  isImageFile,
  checkImageAccessibility,
  useSafeImage,
};